/**
 * Shared shape for every organization we surface on /partners/*.
 *
 * These are independent nonprofits and program administrators. Home Approach is
 * not affiliated with them, is not paid for the referral, and cannot influence
 * whether a member is accepted or what terms they are offered.
 */

export const PARTNER_SITE = "https://www.homeapproach.com";

export type PartnerStep = { title: string; detail: string };
export type PartnerCompareRow = { factor: string; partner: string; stack: string };
export type PartnerFaq = { q: string; a: string };

/** The member barrier a partner is best at removing — drives hub grouping. */
export type PartnerBarrier =
  | "credit"
  | "savings"
  | "certificate"
  | "language"
  | "income_limits";

export type Partner = {
  slug: string;
  /** Short name used in card and hub UI. */
  name: string;
  /** Full legal / spelled-out name. */
  fullName: string;
  path: string;
  externalUrl: string;
  /** Primary outbound action label (e.g. "Find a HOPE Inside location"). */
  externalLabel: string;

  title: string;
  description: string;
  h1: string;

  cardHeadline: string;
  cardBody: string;
  cardPoints: string[];

  barriers: PartnerBarrier[];
  /** One line for the hub grid. */
  hubSummary: string;

  fits: string[];
  doesNotFit: string[];
  steps: PartnerStep[];
  compare: PartnerCompareRow[];
  compareLabel: string;
  faqs: PartnerFaq[];
  disclosure: string;
};

export const BARRIER_LABELS: Record<PartnerBarrier, string> = {
  credit: "Credit is the blocker",
  savings: "No down payment savings",
  certificate: "A program wants an education certificate",
  language: "You want help in your language",
  income_limits: "Income sits below area limits",
};

export const buildPartnerJsonLd = (p: Partner): Record<string, unknown> => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      name: p.title,
      description: p.description,
      url: `${PARTNER_SITE}${p.path}`,
      inLanguage: "en-US",
      isPartOf: { "@type": "WebSite", name: "Home Approach", url: PARTNER_SITE },
      publisher: {
        "@type": "Organization",
        name: "Home Approach",
        url: PARTNER_SITE,
        logo: { "@type": "ImageObject", url: `${PARTNER_SITE}/og-image.png` },
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${PARTNER_SITE}/` },
        { "@type": "ListItem", position: 2, name: "Partners", item: `${PARTNER_SITE}/partners` },
        { "@type": "ListItem", position: 3, name: p.name, item: `${PARTNER_SITE}${p.path}` },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: p.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
});

export const STANDARD_DISCLOSURE = (name: string) =>
  `${name} is an independent organization. Home Approach is not affiliated with it, receives nothing for this referral, and cannot influence whether you're accepted or what terms you're offered. Program details change — confirm everything directly with them before you apply.`;
